// src/pages/UtilisateursPage.js
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useExportExcel } from '../hooks/useExportExcel';

// ── Constantes ────────────────────────────────────────────────
const ROLES = [
  { value: 'direction',    label: 'Direction',      color: '#4a148c', bg: '#f3e5f5' },
  { value: 'super_admin',  label: 'Super Admin',    color: '#b71c1c', bg: '#ffebee' },
  { value: 'admin_rh',     label: 'Admin RH',       color: '#1565C0', bg: '#e3f2fd' },
  { value: 'gestionnaire', label: 'Gestionnaire',   color: '#e65100', bg: '#fff3e0' },
  { value: 'lecteur',      label: 'Lecteur',        color: '#2e7d32', bg: '#e8f5e9' },
];
const STATUTS = ['Actif', 'Inactif', 'Suspendu'];

const roleInfo  = (v) => ROLES.find(r => r.value === v) || { label: v, color: '#546e7a', bg: '#eceff1' };
const fmtDate   = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtDT     = (d) => d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const initForm  = () => ({
  login: '', nom: '', prenoms: '', email: '', telephone: '',
  role: 'lecteur', statut: 'Actif', bureau_id: '', employe_id: '',
  mot_de_passe: '', confirmer_mdp: '',
  force_changement_mdp: true,
});

// ── Composants utilitaires ─────────────────────────────────────

function RoleBadge({ role }) {
  const r = roleInfo(role);
  return (
    <span style={{
      background: r.bg, color: r.color,
      padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{r.label}</span>
  );
}

function StatutBadge({ statut }) {
  const cfg = {
    Actif:    { bg: '#e8f5e9', color: '#2e7d32' },
    Inactif:  { bg: '#eceff1', color: '#546e7a' },
    Suspendu: { bg: '#fff3e0', color: '#e65100' },
  }[statut] || { bg: '#eceff1', color: '#546e7a' };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700,
    }}>{statut}</span>
  );
}

function Alert({ type, msg, onClose }) {
  if (!msg) return null;
  const cfg = {
    success: { bg: '#e8f5e9', color: '#1b5e20', border: '#2e7d32' },
    error:   { bg: '#ffebee', color: '#b71c1c', border: '#c62828' },
    info:    { bg: '#e3f2fd', color: '#0d47a1', border: '#1565C0' },
  }[type] || {};
  return (
    <div style={{
      background: cfg.bg, color: cfg.color,
      borderLeft: `4px solid ${cfg.border}`,
      padding: '10px 16px', borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 16, fontSize: 14,
    }}>
      <span>{msg}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: cfg.color, fontWeight: 700, fontSize: 18 }}>×</button>
    </div>
  );
}

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3200); return () => clearTimeout(t); }, [onClose]);
  const bg = type === 'error' ? '#c62828' : '#2e7d32';
  return (
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
      background: bg, color: '#fff',
      padding: '12px 22px', borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
      fontSize: 14, fontWeight: 600,
      animation: 'fadeIn .2s ease',
    }}>{msg}</div>
  );
}

function Field({ label, required, children, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#546e7a', textTransform: 'uppercase', letterSpacing: '.4px' }}>
        {label}{required && <span style={{ color: '#c62828', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {error && <span style={{ fontSize: 11, color: '#c62828' }}>{error}</span>}
    </div>
  );
}

const inputStyle = {
  border: '1px solid #cfd8dc', borderRadius: 6, padding: '8px 10px',
  fontSize: 13, color: '#1a2a4a', width: '100%', boxSizing: 'border-box',
  outline: 'none', background: '#fff',
};

// ── Modal générique ───────────────────────────────────────────
function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: width,
        maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #e0e7ef',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a2a4a' }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 20, color: '#90a4ae', lineHeight: 1,
          }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

// ── Formulaire création / édition ─────────────────────────────
function UserForm({ user, bureaux, onSave, onClose, saving }) {
  const isEdit = !!user;
  const [form, setForm]     = useState(isEdit ? {
    ...user, mot_de_passe: '', confirmer_mdp: '',
  } : initForm());
  const [errors, setErrors] = useState({});
  const [showPwd, setShowPwd] = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.login.trim())        e.login    = 'Obligatoire';
    if (!form.nom.trim())          e.nom      = 'Obligatoire';
    if (!form.email.trim())        e.email    = 'Obligatoire';
    if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email invalide';
    if (!isEdit && !form.mot_de_passe)    e.mot_de_passe = 'Obligatoire';
    if (form.mot_de_passe && form.mot_de_passe.length < 8) e.mot_de_passe = 'Min. 8 caractères';
    if (form.mot_de_passe && !/[A-Z]/.test(form.mot_de_passe)) e.mot_de_passe = 'Au moins 1 majuscule';
    if (form.mot_de_passe && !/[0-9]/.test(form.mot_de_passe)) e.mot_de_passe = 'Au moins 1 chiffre';
    if (form.mot_de_passe && form.mot_de_passe !== form.confirmer_mdp) e.confirmer_mdp = 'Les mots de passe ne correspondent pas';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = { ...form };
    delete payload.confirmer_mdp;
    if (!payload.mot_de_passe) delete payload.mot_de_passe;
    if (!payload.bureau_id)   payload.bureau_id   = null;
    if (!payload.employe_id)  payload.employe_id  = null;
    onSave(payload);
  };

  const inp = (k, type = 'text', ph = '') => (
    <input
      style={{ ...inputStyle, borderColor: errors[k] ? '#c62828' : '#cfd8dc' }}
      type={type} value={form[k] ?? ''} placeholder={ph}
      onChange={e => set(k, e.target.value)}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Identité */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Nom" required error={errors.nom}>{inp('nom')}</Field>
        <Field label="Prénoms">{inp('prenoms')}</Field>
        <Field label="Login" required error={errors.login}>
          {inp('login', 'text', 'ex: j.dupont')}
        </Field>
        <Field label="Email" required error={errors.email}>{inp('email', 'email')}</Field>
        <Field label="Téléphone">{inp('telephone')}</Field>
        <Field label="Bureau">
          <select style={inputStyle} value={form.bureau_id || ''} onChange={e => set('bureau_id', e.target.value)}>
            <option value="">— Aucun —</option>
            {bureaux.map(b => <option key={b.id} value={b.id}>{b.nom} ({b.ville})</option>)}
          </select>
        </Field>
      </div>

      {/* Rôle & Statut */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Rôle" required>
          <select style={inputStyle} value={form.role} onChange={e => set('role', e.target.value)}>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        <Field label="Statut">
          <select style={inputStyle} value={form.statut} onChange={e => set('statut', e.target.value)}>
            {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>

      {/* Mot de passe */}
      <div style={{ background: '#f7f9fc', border: '1px solid #e0e7ef', borderRadius: 8, padding: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#546e7a', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 10 }}>
          {isEdit ? '🔑 Nouveau mot de passe (laisser vide pour ne pas changer)' : '🔑 Mot de passe initial'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Mot de passe" required={!isEdit} error={errors.mot_de_passe}>
            <div style={{ position: 'relative' }}>
              <input
                style={{ ...inputStyle, paddingRight: 36, borderColor: errors.mot_de_passe ? '#c62828' : '#cfd8dc' }}
                type={showPwd ? 'text' : 'password'}
                value={form.mot_de_passe}
                onChange={e => set('mot_de_passe', e.target.value)}
                placeholder="Min. 8 car., 1 maj., 1 chiffre"
              />
              <button type="button" onClick={() => setShowPwd(s => !s)} style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#90a4ae', fontSize: 15,
              }}>{showPwd ? '🙈' : '👁️'}</button>
            </div>
          </Field>
          <Field label="Confirmer" error={errors.confirmer_mdp}>
            <input
              style={{ ...inputStyle, borderColor: errors.confirmer_mdp ? '#c62828' : '#cfd8dc' }}
              type={showPwd ? 'text' : 'password'}
              value={form.confirmer_mdp}
              onChange={e => set('confirmer_mdp', e.target.value)}
              placeholder="Répéter le mot de passe"
            />
          </Field>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, cursor: 'pointer', color: '#37474f' }}>
          <input type="checkbox" checked={!!form.force_changement_mdp} onChange={e => set('force_changement_mdp', e.target.checked)} />
          Forcer le changement de mot de passe à la prochaine connexion
        </label>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
        <button onClick={onClose} style={{
          background: '#fff', color: '#1565C0', border: '1.5px solid #1565C0',
          borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Annuler</button>
        <button onClick={handleSubmit} disabled={saving} style={{
          background: '#1565C0', color: '#fff', border: 'none',
          borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600,
          cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? .7 : 1,
        }}>{saving ? '⏳ Enregistrement…' : isEdit ? '💾 Mettre à jour' : '✅ Créer le compte'}</button>
      </div>
    </div>
  );
}

// ── Modal reset MDP ───────────────────────────────────────────
function ResetPwdModal({ user, onSave, onClose, saving }) {
  const [mdp, setMdp]     = useState('');
  const [conf, setConf]   = useState('');
  const [force, setForce] = useState(true);
  const [show, setShow]   = useState(false);
  const [err, setErr]     = useState('');

  const handleSubmit = () => {
    if (mdp.length < 8)            return setErr('Minimum 8 caractères');
    if (!/[A-Z]/.test(mdp))        return setErr('Au moins 1 majuscule');
    if (!/[0-9]/.test(mdp))        return setErr('Au moins 1 chiffre');
    if (mdp !== conf)              return setErr('Les mots de passe ne correspondent pas');
    setErr('');
    onSave({ mot_de_passe: mdp, force_changement_mdp: force });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 13, color: '#546e7a' }}>
        Réinitialisation du mot de passe de <strong>{user.nom} {user.prenoms || ''}</strong> ({user.login})
      </p>
      {err && <div style={{ color: '#c62828', fontSize: 13, padding: '8px 12px', background: '#ffebee', borderRadius: 6 }}>{err}</div>}
      <Field label="Nouveau mot de passe" required>
        <div style={{ position: 'relative' }}>
          <input
            style={{ ...inputStyle, paddingRight: 36 }}
            type={show ? 'text' : 'password'}
            value={mdp} onChange={e => setMdp(e.target.value)}
            placeholder="Min. 8 car., 1 maj., 1 chiffre"
          />
          <button type="button" onClick={() => setShow(s => !s)} style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: '#90a4ae',
          }}>{show ? '🙈' : '👁️'}</button>
        </div>
      </Field>
      <Field label="Confirmer">
        <input style={inputStyle} type={show ? 'text' : 'password'}
          value={conf} onChange={e => setConf(e.target.value)} placeholder="Répéter" />
      </Field>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} />
        Forcer le changement à la prochaine connexion
      </label>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
        <button onClick={onClose} style={{
          background: '#fff', color: '#1565C0', border: '1.5px solid #1565C0',
          borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Annuler</button>
        <button onClick={handleSubmit} disabled={saving} style={{
          background: '#e65100', color: '#fff', border: 'none',
          borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600,
          cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? .7 : 1,
        }}>{saving ? '⏳…' : '🔑 Réinitialiser'}</button>
      </div>
    </div>
  );
}

// ── Modal détail / activité ───────────────────────────────────
function UserDetailModal({ user, onClose }) {
  const [logs, setLogs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const r = roleInfo(user.role);

  useEffect(() => {
    api.get(`/utilisateurs/${user.id}/activite`)
      .then(res => setLogs(res.data?.data?.logs || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [user.id]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Carte identité */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: r.bg, color: r.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 700, flexShrink: 0,
        }}>
          {(user.nom?.[0] || '?').toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#1a2a4a' }}>{user.nom} {user.prenoms || ''}</div>
          <div style={{ fontSize: 13, color: '#607d8b' }}>@{user.login} · {user.email}</div>
          <div style={{ marginTop: 4, display: 'flex', gap: 8 }}>
            <RoleBadge role={user.role} />
            <StatutBadge statut={user.statut} />
          </div>
        </div>
      </div>

      {/* Infos */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
        {[
          ['Téléphone',          user.telephone || '—'],
          ['Bureau',             user.bureau_nom || '—'],
          ['Employé lié',        user.employe_nom || '—'],
          ['Dernière connexion', fmtDT(user.derniere_connexion)],
          ['Nb connexions',      user.nb_connexions ?? 0],
          ['Créé le',            fmtDate(user.created_at)],
        ].map(([k, v]) => (
          <div key={k} style={{ background: '#f7f9fc', borderRadius: 6, padding: '8px 12px' }}>
            <div style={{ fontSize: 11, color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{k}</div>
            <div style={{ color: '#1a2a4a', fontWeight: 500 }}>{String(v)}</div>
          </div>
        ))}
      </div>

      {/* Activité */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a2a4a', marginBottom: 8 }}>Activité récente</div>
        {loading
          ? <div style={{ color: '#90a4ae', fontSize: 13, padding: '12px 0' }}>Chargement…</div>
          : logs.length === 0
            ? <div style={{ color: '#90a4ae', fontSize: 13, fontStyle: 'italic' }}>Aucune activité enregistrée</div>
            : (
              <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e7ef', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f7f9fc' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#546e7a', fontWeight: 600 }}>Action</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#546e7a', fontWeight: 600 }}>IP</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: '#546e7a', fontWeight: 600 }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((l, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #f1f4f8' }}>
                        <td style={{ padding: '6px 10px' }}>{l.action}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{l.ip_address || '—'}</td>
                        <td style={{ padding: '6px 10px', color: '#90a4ae' }}>{fmtDT(l.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        }
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{
          background: '#1565C0', color: '#fff', border: 'none',
          borderRadius: 7, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Fermer</button>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function UtilisateursPage() {
  const [users,    setUsers]    = useState([]);
  const [bureaux,  setBureaux]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [alert,    setAlert]    = useState(null);
  const [toast,    setToast]    = useState(null);
  const { exporting, exportToExcel } = useExportExcel();

  // Filtres
  const [search,   setSearch]   = useState('');
  const [filterRole,   setFilterRole]   = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [editUser,   setEditUser]   = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [resetUser,  setResetUser]  = useState(null);
  const [confirmSuspend, setConfirmSuspend] = useState(null); // { user, newStatut }

  const LIMIT = 25;

  const showToast = (msg, type = 'success') => setToast({ msg, type });
  const showAlert = (type, msg) => setAlert({ type, msg });

  // ── Chargement ───────────────────────────────────────────────
  const loadUsers = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: LIMIT });
      if (search)       params.append('q',      search);
      if (filterRole)   params.append('role',   filterRole);
      if (filterStatut) params.append('statut', filterStatut);
      const { data } = await api.get(`/utilisateurs?${params}`);
      const res = data.data ?? data;
      const list = Array.isArray(res) ? res : (res.data ?? res.users ?? []);
      setUsers(list);
      setTotal(res.total ?? res.count ?? data.total ?? data.count ?? list.length);
      setPage(p);
    } catch (err) {
      showAlert('error', err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [search, filterRole, filterStatut]);

  useEffect(() => { loadUsers(1); }, [loadUsers]);

  useEffect(() => {
    api.get('/bureaux').then(r => setBureaux(r.data?.data ?? r.data ?? [])).catch(() => {});
  }, []);

  // ── CRUD ─────────────────────────────────────────────────────
  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await api.post('/utilisateurs', form);
      showToast('Compte créé avec succès ✓');
      setShowCreate(false);
      loadUsers(1);
    } catch (err) {
      showAlert('error', err.response?.data?.error || err.message);
    } finally { setSaving(false); }
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      const { mot_de_passe, confirmer_mdp, ...info } = form;
      await api.put(`/utilisateurs/${editUser.id}`, info);
      if (mot_de_passe) {
        await api.put(`/utilisateurs/${editUser.id}/mot-de-passe`, {
          mot_de_passe, force_changement_mdp: form.force_changement_mdp,
        });
      }
      showToast('Compte mis à jour ✓');
      setEditUser(null);
      loadUsers(page);
    } catch (err) {
      showAlert('error', err.response?.data?.error || err.message);
    } finally { setSaving(false); }
  };

  const handleResetPwd = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/utilisateurs/${resetUser.id}/mot-de-passe`, payload);
      showToast('Mot de passe réinitialisé ✓');
      setResetUser(null);
    } catch (err) {
      showAlert('error', err.response?.data?.error || err.message);
    } finally { setSaving(false); }
  };

  const handleChangerStatut = async (userId, newStatut) => {
    setSaving(true);
    try {
      await api.put(`/utilisateurs/${userId}/statut`, { statut: newStatut });
      showToast(`Compte ${newStatut.toLowerCase()} ✓`);
      setConfirmSuspend(null);
      loadUsers(page);
    } catch (err) {
      showAlert('error', err.response?.data?.error || err.message);
    } finally { setSaving(false); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer le compte de ${user.nom} ${user.prenoms || ''} (@${user.login}) ?\nCette action est irréversible.`)) return;
    setSaving(true);
    try {
      await api.delete(`/utilisateurs/${user.id}`);
      showToast('Compte supprimé');
      loadUsers(page);
    } catch (err) {
      showAlert('error', err.response?.data?.error || err.message);
    } finally { setSaving(false); }
  };

  const handleReinitSession = async (user) => {
    if (!window.confirm(`Déconnecter ${user.nom} ${user.prenoms || ''} de toutes ses sessions actives ?`)) return;
    try {
      await api.post(`/utilisateurs/${user.id}/reinitialiser-session`);
      showToast(`Sessions de @${user.login} invalidées ✓`);
    } catch (err) {
      showToast('Erreur lors de l\'invalidation', 'error');
    }
  };

  // ── Statistiques rapides ──────────────────────────────────────
  const stats = {
    total:     total,
    actifs:    users.filter(u => u.statut === 'Actif').length,
    admins:    users.filter(u => ['direction','super_admin','admin_rh'].includes(u.role)).length,
    suspendus: users.filter(u => u.statut === 'Suspendu').length,
  };

  // ── Rendu ─────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        .usr-row:hover td { background: #f7f9fc !important; }
        .usr-btn-icon { background:none; border:none; cursor:pointer; padding:5px 7px; border-radius:5px; transition:background .12s; color:#607d8b; font-size:15px; }
        .usr-btn-icon:hover { background:#e3f2fd; color:#1565C0; }
        .usr-btn-icon.danger:hover { background:#ffebee; color:#c62828; }
        .usr-btn-icon:disabled { opacity:.4; cursor:not-allowed; }
      `}</style>

      {/* Toast */}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a2a4a' }}>👤 Utilisateurs système</h2>
          <div style={{ fontSize: 13, color: '#607d8b', marginTop: 2 }}>Gestion des comptes et des accès GFM3</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={() => exportToExcel({
              rows: users,
              filename: `GFM3_Utilisateurs_${new Date().toISOString().slice(0,10)}.xlsx`,
              sheetName: 'Utilisateurs',
              title: 'Liste des utilisateurs — GFM3 RH',
              columns: [
                { key: 'login',            label: 'Login',            width: 20 },
                { key: 'nom',              label: 'Nom',              width: 22 },
                { key: 'prenoms',          label: 'Prénoms',          width: 25 },
                { key: 'email',            label: 'Email',            width: 30 },
                { key: 'telephone',        label: 'Téléphone',        width: 16 },
                { key: 'role',             label: 'Rôle',             width: 18 },
                { key: 'statut',           label: 'Statut',           width: 14 },
                { key: 'derniere_connexion', label: 'Dernière connexion', width: 20, format: fmtDate },
                { key: 'created_at',       label: 'Créé le',          width: 16, format: fmtDate },
              ],
            })}
            disabled={exporting || !users.length}
            style={{
              background: '#2e7d32', color: '#fff', border: 'none',
              borderRadius: 7, padding: '10px 16px', fontSize: 13, fontWeight: 700,
              cursor: users.length ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', gap: 6,
              opacity: exporting ? .7 : 1,
            }}
          >
            {exporting ? '⏳' : '📊'} Exporter Excel
          </button>
          <button onClick={() => setShowCreate(true)} style={{
            background: '#1565C0', color: '#fff', border: 'none',
            borderRadius: 7, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>+ Nouveau compte</button>
        </div>
      </div>

      {/* Alerte */}
      {alert && <Alert type={alert.type} msg={alert.msg} onClose={() => setAlert(null)} />}

      {/* Stats rapides */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total comptes',  value: stats.total,   color: '#1565C0', bg: '#e3f2fd', icon: '👥' },
          { label: 'Actifs',         value: stats.actifs,           color: '#2e7d32', bg: '#e8f5e9', icon: '✅' },
          { label: 'Administrateurs',value: stats.admins,           color: '#b71c1c', bg: '#ffebee', icon: '🔐' },
          { label: 'Suspendus',      value: stats.suspendus,        color: '#e65100', bg: '#fff3e0', icon: '⏸️' },
        ].map(s => (
          <div key={s.label} style={{
            background: s.bg, borderRadius: 10, padding: '14px 16px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 22 }}>{s.icon}</span>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: s.color, opacity: .8, marginTop: 2 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div style={{
        background: '#fff', border: '1px solid #e0e7ef', borderRadius: 10,
        padding: '14px 16px', marginBottom: 16,
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <input
          style={{ ...inputStyle, maxWidth: 280 }}
          placeholder="🔍 Rechercher par nom, login, email…"
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <select style={{ ...inputStyle, maxWidth: 180 }} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="">Tous les rôles</option>
          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select style={{ ...inputStyle, maxWidth: 150 }} value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(search || filterRole || filterStatut) && (
          <button onClick={() => { setSearch(''); setFilterRole(''); setFilterStatut(''); }} style={{
            background: 'none', border: '1px solid #cfd8dc', borderRadius: 6,
            padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: '#546e7a',
          }}>✕ Réinitialiser</button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#90a4ae' }}>
          {total} compte{total > 1 ? 's' : ''}
        </span>
      </div>

      {/* Tableau */}
      <div style={{ background: '#fff', border: '1px solid #e0e7ef', borderRadius: 10, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 60, justifyContent: 'center', color: '#607d8b' }}>
            <div style={{ width: 28, height: 28, border: '3px solid #e0e7ef', borderTopColor: '#1565C0', borderRadius: '50%', animation: 'pm-spin .7s linear infinite' }} />
            Chargement…
          </div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#90a4ae' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>👤</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Aucun utilisateur trouvé</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Ajustez les filtres ou créez un premier compte.</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f7f9fc' }}>
                {['Utilisateur', 'Login', 'Rôle', 'Statut', 'Bureau', 'Dernière connexion', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: '.4px',
                    color: '#546e7a', borderBottom: '2px solid #e0e7ef', fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const r = roleInfo(u.role);
                const initiale = (u.nom?.[0] || '?').toUpperCase();
                return (
                  <tr key={u.id} className="usr-row">
                    {/* Utilisateur */}
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f4f8' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 34, height: 34, borderRadius: '50%',
                          background: r.bg, color: r.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 800, fontSize: 14, flexShrink: 0,
                        }}>{initiale}</div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#1a2a4a' }}>{u.nom} {u.prenoms || ''}</div>
                          <div style={{ fontSize: 11, color: '#90a4ae' }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    {/* Login */}
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f4f8', fontFamily: 'monospace', color: '#546e7a', fontSize: 12 }}>
                      @{u.login}
                    </td>
                    {/* Rôle */}
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f4f8' }}>
                      <RoleBadge role={u.role} />
                    </td>
                    {/* Statut */}
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f4f8' }}>
                      <StatutBadge statut={u.statut} />
                    </td>
                    {/* Bureau */}
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f4f8', fontSize: 12, color: '#546e7a' }}>
                      {u.bureau_nom || <span style={{ color: '#cfd8dc' }}>—</span>}
                    </td>
                    {/* Dernière connexion */}
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f4f8', fontSize: 12, color: '#90a4ae', whiteSpace: 'nowrap' }}>
                      {fmtDT(u.derniere_connexion)}
                    </td>
                    {/* Actions */}
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #f1f4f8', whiteSpace: 'nowrap' }}>
                      {/* Voir détail */}
                      <button className="usr-btn-icon" title="Voir le détail" onClick={() => setDetailUser(u)}>
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                        </svg>
                      </button>
                      {/* Modifier */}
                      <button className="usr-btn-icon" title="Modifier" onClick={() => setEditUser(u)} style={{ color: '#1565C0' }}>
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                      </button>
                      {/* Reset MDP */}
                      <button className="usr-btn-icon" title="Réinitialiser le mot de passe" onClick={() => setResetUser(u)}>
                        🔑
                      </button>
                      {/* Suspendre / Activer */}
                      {u.statut === 'Actif'
                        ? <button className="usr-btn-icon" title="Suspendre" onClick={() => setConfirmSuspend({ user: u, newStatut: 'Suspendu' })}>⏸️</button>
                        : <button className="usr-btn-icon" title="Activer" style={{ color: '#2e7d32' }} onClick={() => handleChangerStatut(u.id, 'Actif')}>▶️</button>
                      }
                      {/* Invalider sessions */}
                      <button className="usr-btn-icon" title="Déconnecter toutes les sessions" onClick={() => handleReinitSession(u)}>
                        🔒
                      </button>
                      {/* Supprimer */}
                      <button className="usr-btn-icon danger" title="Supprimer le compte" onClick={() => handleDelete(u)}>
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {total > LIMIT && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #f1f4f8', fontSize: 13 }}>
            <span style={{ color: '#607d8b' }}>
              Page {page} / {Math.ceil(total / LIMIT)} — {total} compte{total > 1 ? 's' : ''}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button disabled={page <= 1} onClick={() => loadUsers(page - 1)} style={{
                padding: '6px 14px', border: '1px solid #cfd8dc', borderRadius: 6,
                background: page <= 1 ? '#f7f9fc' : '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer',
                fontSize: 13, color: page <= 1 ? '#cfd8dc' : '#1565C0',
              }}>← Précédent</button>
              <button disabled={page >= Math.ceil(total / LIMIT)} onClick={() => loadUsers(page + 1)} style={{
                padding: '6px 14px', border: '1px solid #cfd8dc', borderRadius: 6,
                background: page >= Math.ceil(total / LIMIT) ? '#f7f9fc' : '#fff',
                cursor: page >= Math.ceil(total / LIMIT) ? 'not-allowed' : 'pointer',
                fontSize: 13, color: page >= Math.ceil(total / LIMIT) ? '#cfd8dc' : '#1565C0',
              }}>Suivant →</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ─────────────────────────────────────────── */}

      {showCreate && (
        <Modal title="➕ Nouveau compte utilisateur" onClose={() => setShowCreate(false)} width={620}>
          <UserForm bureaux={bureaux} onSave={handleCreate} onClose={() => setShowCreate(false)} saving={saving} />
        </Modal>
      )}

      {editUser && (
        <Modal title={`✏️ Modifier — ${editUser.nom} ${editUser.prenoms || ''}`} onClose={() => setEditUser(null)} width={620}>
          <UserForm user={editUser} bureaux={bureaux} onSave={handleUpdate} onClose={() => setEditUser(null)} saving={saving} />
        </Modal>
      )}

      {detailUser && (
        <Modal title={`👤 ${detailUser.nom} ${detailUser.prenoms || ''}`} onClose={() => setDetailUser(null)} width={560}>
          <UserDetailModal user={detailUser} onClose={() => setDetailUser(null)} />
        </Modal>
      )}

      {resetUser && (
        <Modal title="🔑 Réinitialiser le mot de passe" onClose={() => setResetUser(null)} width={460}>
          <ResetPwdModal user={resetUser} onSave={handleResetPwd} onClose={() => setResetUser(null)} saving={saving} />
        </Modal>
      )}

      {confirmSuspend && (
        <Modal title="⏸️ Suspendre ce compte ?" onClose={() => setConfirmSuspend(null)} width={420}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: '#37474f', lineHeight: 1.6 }}>
              L'utilisateur <strong>{confirmSuspend.user.nom} {confirmSuspend.user.prenoms || ''}</strong> (@{confirmSuspend.user.login}) ne pourra plus se connecter tant que son compte est suspendu.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setConfirmSuspend(null)} style={{
                background: '#fff', color: '#546e7a', border: '1.5px solid #cfd8dc',
                borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>Annuler</button>
              <button onClick={() => handleChangerStatut(confirmSuspend.user.id, confirmSuspend.newStatut)} disabled={saving} style={{
                background: '#e65100', color: '#fff', border: 'none',
                borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}>⏸️ Suspendre</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
